import { useState } from "react";

import { ApiError } from "../api/client";

/**
 * Cancel, or put back.
 *
 * Cancelling asks first; reinstating does not. That asymmetry is deliberate
 * — one of them changes what the record says happened, the other undoes a
 * mistake, and putting a confirmation in front of the undo makes people
 * hesitate to correct themselves.
 *
 * The server refuses to cancel anything that still has work in it and says
 * what is in the way. That message is shown as-is rather than replaced with
 * something vaguer, because "1 deliverable and 1 task" tells you what to do
 * next and "cannot cancel" does not.
 */
export function CancelToggle({
  what,
  cancelledAt,
  onToggle,
}: {
  what: "project" | "deliverable";
  cancelledAt: string | null;
  onToggle: (cancelled: boolean) => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (next: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onToggle(next);
      setConfirming(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  if (cancelledAt) {
    return (
      <>
        <span className="chip cancelled">Cancelled</span>
        <button onClick={() => void run(false)} disabled={busy}>
          Reinstate
        </button>
        {error ? <span className="error-inline">{error}</span> : null}
      </>
    );
  }

  if (confirming) {
    return (
      <>
        <span className="muted">Cancel this {what}?</span>
        <button className="rework" onClick={() => void run(true)} disabled={busy}>
          Yes, cancel it
        </button>
        <button onClick={() => setConfirming(false)} disabled={busy}>
          Keep it
        </button>
      </>
    );
  }

  return (
    <>
      <button onClick={() => setConfirming(true)} disabled={busy}>
        Cancel {what}
      </button>
      {error ? <span className="error-inline">{error}</span> : null}
    </>
  );
}

/**
 * Cancel a TASK, or put it back.
 *
 * Separate from `CancelToggle` because a task demands something a container
 * does not: a REASON, every time, enforced by a database constraint. So the
 * confirmation step is a box to type in rather than a yes/no — the friction is
 * the point, and it is the only moment the answer can be captured. Nobody can
 * reconstruct six months later why a piece of work stopped.
 *
 * The reason is then SHOWN wherever the cancellation is, for the same reason.
 * A required field nobody ever reads back teaches people to type "x".
 *
 * Reinstating asks nothing. One of these changes what the record says
 * happened; the other undoes a mistake, and putting a hurdle in front of the
 * undo makes people hesitate to correct themselves.
 */
export function CancelTaskControl({
  cancelledAt,
  cancelReason,
  mayCancel,
  onToggle,
}: {
  cancelledAt: string | null;
  cancelReason: string | null;
  /**
   * Whether this person may cancel or reinstate it (E9.5) — `editability.cancel`,
   * the server's own answer. When false there is no button: a button that
   * only ever produces a refusal reads as the app breaking (U.1). A cancelled
   * task still SHOWS that it was cancelled, and why, to everybody.
   */
  mayCancel: boolean;
  onToggle: (cancelled: boolean, reason?: string) => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (next: boolean, why?: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onToggle(next, why);
      setConfirming(false);
      setReason("");
    } catch (e) {
      // Shown verbatim. The server's refusals name the person who may act or
      // the thing in the way, and replacing that with "could not cancel"
      // throws away the only part that tells you what to do next.
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (cancelledAt) {
    return (
      <div className="cancelled-task">
        <span className="chip cancelled">Cancelled</span>
        {cancelReason ? <span className="cancel-reason">{cancelReason}</span> : null}
        {mayCancel ? (
          <button onClick={() => void run(false)} disabled={busy}>
            Reinstate
          </button>
        ) : null}
        {error ? <span className="error-inline">{error}</span> : null}
      </div>
    );
  }

  if (!mayCancel) return null;

  if (confirming) {
    return (
      <div className="cancel-task-form">
        <label htmlFor="cancel-reason">Why is this being cancelled?</label>
        <textarea
          id="cancel-reason"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Client withdrew the scope, duplicate of another task, …"
          autoFocus
        />
        <div className="row">
          <button
            className="rework"
            onClick={() => void run(true, reason)}
            // Disabled rather than left clickable to fail: the rule is known
            // here, so making someone submit to be told is pointless.
            disabled={busy || reason.trim().length === 0}
          >
            Cancel this task
          </button>
          <button
            onClick={() => {
              setConfirming(false);
              setReason("");
            }}
            disabled={busy}
          >
            Keep it
          </button>
        </div>
        {error ? <span className="error-inline">{error}</span> : null}
      </div>
    );
  }

  return (
    <>
      <button onClick={() => setConfirming(true)} disabled={busy}>
        Cancel task
      </button>
      {error ? <span className="error-inline">{error}</span> : null}
    </>
  );
}

/**
 * Close as done, or reopen.
 *
 * Separate control from Cancel on purpose. "We did it" and "we are not doing
 * it" are different things to have to look back on, and one button that did
 * both would lose that the moment anyone needed the answer.
 *
 * The server owns both rules — only a PM, and only when every task is Done —
 * and its refusal is shown verbatim, because "3 tasks are still open" tells
 * you what to do and "cannot complete" does not.
 */
export function CompleteToggle({
  what,
  completedAt,
  disabled,
  onToggle,
}: {
  what: "project" | "deliverable";
  completedAt: string | null;
  disabled?: boolean;
  onToggle: (completed: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (next: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onToggle(next);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (completedAt) {
    return (
      <>
        <span className="chip completed">Completed</span>
        <button onClick={() => void run(false)} disabled={busy}>
          Reopen
        </button>
        {error ? <span className="error-inline">{error}</span> : null}
      </>
    );
  }

  return (
    <>
      <button onClick={() => void run(true)} disabled={busy || disabled}>
        Mark {what} complete
      </button>
      {error ? <span className="error-inline">{error}</span> : null}
    </>
  );
}
